// src/components/discussion/DiscussionHeader.jsx
import { useState } from "react";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { SearchBox } from "../SearchBox";
import PinnedMessages from "./PinnedMessages";

/**
 * Шапка обсуждения. Две строки:
 *   1. закреплённые сообщения (если есть);
 *   2. панель действий - Back (опц.), кнопки-переключатели режимов,
 *      поиск.
 *
 * Разделение на строки принципиальное: pinned-сообщения растягиваются
 * на всю ширину шапки и не отбирают место у кнопок. Если pinned нет,
 * PinnedMessages возвращает null и не создаёт DOM-узла - flex-column
 * не оставляет пустой строки, вторая строка становится первой.
 *
 * viewTabs - массив { id, label, count }. Активный режим подсвечен
 * классом .active. Повторный клик по активной кнопке возвращает в чат
 * (поведение реализовано в родителе через onViewChange).
 *
 * showSearch - при активном режиме «Файлы»/«Ссылки» панель поиска
 * скрывается: она относится к сообщениям, а не к файлам/ссылкам.
 *
 * Кнопка «Назад» появляется слева от кнопок режимов, когда активен
 * не-чат. Разметка та же, что у кнопки «Назад» в FileManager и
 * ModalShell (.btn.ghost.sm + Ic ICONS.left).
 */
export default function DiscussionHeader({
  pinned,
  onJump,
  onSearchChange,
  totalMatches = 0,
  currentMatch = 0,
  onPrevMatch,
  onNextMatch,
  viewTabs = [],
  activeView = "chat",
  onViewChange,
  showSearch = true,
}) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const toggle = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false);
      setQuery("");
      onSearchChange("");
    } else {
      setIsSearchOpen(true);
    }
  };

  const update = (v) => {
    setQuery(v);
    onSearchChange(v);
  };

  const showBack = activeView !== "chat";

  return (
    <div className="discussion-header">
      <PinnedMessages pinned={pinned} onJump={onJump} />

      <div className="discussion-actions">
        {showBack && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => onViewChange("chat")}
            title="Вернуться к обсуждению"
          >
            <Ic d={ICONS.left} size={14} /> Назад
          </button>
        )}

        {viewTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`icon-btn${activeView === t.id ? " active" : ""}`}
            onClick={() => onViewChange(t.id)}
            aria-pressed={activeView === t.id}
            title={t.label}
            aria-label={t.label}
          >
            {t.count != null ? `${t.label} (${t.count})` : t.label}
          </button>
        ))}

        {showSearch && (
          <div className="search-toggle-group">
            <button
              className={`icon-btn ${isSearchOpen ? "active" : ""}`}
              onClick={toggle}
              title={isSearchOpen ? "Закрыть поиск" : "Поиск"}
            >
              <Ic d={ICONS.search} size={18} />
            </button>

            {isSearchOpen && (
              <div className="chat-search-inline">
                <SearchBox
                  value={query}
                  onChange={update}
                  placeholder="Поиск по обсуждению..."
                  autoFocus
                />

                {query && (
                  <div className="search-navigation">
                    <span className="search-result-count">
                      {currentMatch} / {totalMatches}
                    </span>
                    {totalMatches > 0 && (
                      <>
                        <button
                          type="button"
                          className="icon-btn xs"
                          onClick={onPrevMatch}
                          title="Предыдущее совпадение"
                        >
                          <Ic d={ICONS.up} size={14} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn xs"
                          onClick={onNextMatch}
                          title="Следующее совпадение"
                        >
                          <Ic d={ICONS.down} size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
